import { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FileEntry, Form } from "capitalrx-components-2.0/lib/forms";
import { LoadingGroup, LoadingSkeleton, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";

import {
  DirectMemberReimbursementCompoundPrescriptionInformation,
  DirectMemberReimbursementCoordinationOfBenefits,
  DirectMemberReimbursementPharmacyDetailsReason,
  DirectMemberReimbursementPharmacyPrescription,
  DirectMemberReimbursementSubmissionRequest,
  DirectMemberReimbursementVaccineInformation,
} from "@/App/api/directMemberReimbursement";
import { DMRDrug, useListDMRDrugsQuery } from "@/App/api/drug";
import { Pharmacy, useListDMRPharmaciesQuery } from "@/App/api/pharmacy";
import { Provider, useListProvidersQuery } from "@/App/api/provider";
import { Textbox, ThemedButton, ThemedCheckboxGroup, ThemedSubmitButton } from "@/App/components";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { DirectMemberReimbursementAcknowledgeField } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementAcknowledgeField";
import {
  DirectMemberReimbursementCompoundPrescriptionForm,
  DirectMemberReimbursementCompoundPrescriptionFormHandle,
} from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementCompoundPrescriptionForm";
import { DirectMemberReimbursementContactInformationFields } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementContactInformationFields";
import {
  DirectMemberReimbursementCoordinationOfBenefitsForm,
  DirectMemberReimbursementCoordinationOfBenefitsFormHandle,
} from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementCoordinationOfBenefitsForm";
import { DirectMemberReimbursementNoResults } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementNoResults";
import {
  DirectMemberReimbursementProviderDetailsCard,
  ProviderDetails,
} from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementPharmacyDetailsCard";
import { DirectMemberReimbursementPharmacySearchResults } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementPharmacySearchResults";
import { DirectMemberReimbursementPrescriptionVaccineEquipmentCard } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementPrescriptionVaccineEquipmentCard";
import { DirectMemberReimbursementPrescriptionVaccineEquipmentSearchResults } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementPrescriptionVaccineEquipmentSearchResults";
import { DirectMemberReimbursementUploadClaimEvidenceField } from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementUploadClaimEvidenceField";
import {
  DirectMemberReimbursementVaccineInformationForm,
  DirectMemberReimbursementVaccineInformationFormHandle,
} from "@/App/pages/DirectMemberReimbursement/DirectMemberReimbursementVaccineInformationForm";
import { queryClient } from "@/index";

import "./DirectMemberReimbursementClaimDetailsForm.scss";
import {
  DirectMemberReimbursementAdditionalPrescriptionInformationForm,
  DirectMemberReimbursementAdditionalPrescriptionInformationFormHandle,
} from "./DirectMemberReimbursementAdditionalPrescriptionInformationForm";

const TRANSLATION_PATH = "DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementClaimDetailsForm";

const PHARMACY_RESULTS_PER_PAGE = 5;

const REASONS = {
  id_card_unavailable: "idCardUnavailable",
  out_of_network_due_to_travel: "outOfNetworkDueToTravel",
  out_of_network_due_to_vaccine: "outOfNetworkDueToVaccine",
  out_of_network_due_to_no_network_pharmacy: "outOfNetworkDueToNoNetworkPharmacy",
  out_of_network_due_to_federal_emergency: "outOfNetworkDueToFederalEmergency",
  out_of_network_due_to_out_of_stock: "outOfNetworkDueToOutOfStock",
  purchased_while_awaiting_approval: "purchasedWhileAwaitingApproval",
  pharmacy_billed_incorrect_insurance: "pharmacyBilledIncorrectInsurance",
  compound_prescription: "compoundPrescription",
  purchased_outside_of_usa: "purchasedOutsideOfUSA",
  coordination_of_benefits: "coordinationOfBenefits",
  received_vaccine_in_outpatient_setting: "receivedVaccineInOutpatientSetting",
  other: "other",
} as const;

export interface DMRDetailsForm {
  readonly reasons?: DirectMemberReimbursementPharmacyDetailsReason[];
  readonly otherReasonText?: string;
}

interface DMRPrescriberSearchForm {
  readonly npi?: string;
}

interface DMRPharmacySupplierSearchForm extends DMRPrescriberSearchForm {
  readonly name?: string;
  readonly address?: string;
  readonly zip?: string;
  readonly page_number?: number;
  readonly results_per_page?: number;
}

interface DMRPrescriptionVaccineEquipmentSearchForm {
  readonly ndc?: string;
}

interface ClaimEvidenceForm {
  readonly claim_evidence_files: FileEntry[];
}

interface ContactInformationForm {
  readonly email_address: string;
  readonly phone_number: string;
}

interface AcknowledgeForm {
  readonly acknowledge: boolean;
}

interface DirectMemberReimbursementClaimDetailsFormProps {
  readonly isSubmitting: boolean;
  readonly onSubmitCallbackHandler: (result: {
    payload: Partial<DirectMemberReimbursementSubmissionRequest>;
    documents: FileEntry[];
  }) => void;
}

export const DirectMemberReimbursementClaimDetailsForm: FC<DirectMemberReimbursementClaimDetailsFormProps> = ({
  isSubmitting, onSubmitCallbackHandler,
}) => {
  const { t: translate } = useTranslation();
  const [pharmacyResultsPageNumber, setPharmacyResultsPageNumber] = useState<number>(1);
  const [shouldRefetchPharmacies, setShouldRefetchPharmacies] = useState<boolean>(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState<ProviderDetails | null>(null);
  const [pharmacyNotSelectedError, setPharmacyNotSelectedError] = useState<boolean>(false);
  const [selectedPrescriber, setSelectedPrescriber] = useState<ProviderDetails | null>(null);
  const [prescriberNotSelectedError, setPrescriberNotSelectedError] = useState<boolean>(false);
  const [selectedPrescriptionVaccineEquipment, setSelectedPrescriptionVaccineEquipment] = useState<DMRDrug | null>(null);
  const [prescriptionNotSelectedError, setPrescriptionNotSelectedError] = useState<boolean>(false);
  const [submittingPayload, setSubmittingPayload] = useState<boolean>(false);
  const dmrAdditionalPrescriptionInformationFormRef = useRef<DirectMemberReimbursementAdditionalPrescriptionInformationFormHandle>(null);
  const dmrCompoundPrescriptionFormRef = useRef<DirectMemberReimbursementCompoundPrescriptionFormHandle>(null);
  const dmrCoordinationOfBenefitsFormRef = useRef<DirectMemberReimbursementCoordinationOfBenefitsFormHandle>(null);
  const dmrVaccineInformationFormRef = useRef<DirectMemberReimbursementVaccineInformationFormHandle>(null);
  // Form anchor points for validation
  const dmrPharmacyAnchorRef = useRef<HTMLSpanElement>(null);
  const dmrPrescriberAnchorRef = useRef<HTMLSpanElement>(null);
  const dmrPrescriptionAnchorRef = useRef<HTMLSpanElement>(null);
  const dmrEvidenceClaimAnchorRef = useRef<HTMLSpanElement>(null);
  const dmrContactInformationAnchorRef = useRef<HTMLSpanElement>(null);
  const dmrAcknowledgeAnchorRef = useRef<HTMLSpanElement>(null);
  const showAlert = useAlert();

  const isEmulating = !!sessionStorage.getItem("isEmulation");

  // Form Driven Pharmacy Search Data
  const pharmacySupplierSearchForm = useForm<DMRPharmacySupplierSearchForm>({ defaultValues: {} });

  const pharmacyNpi = pharmacySupplierSearchForm.watch("npi");
  const pharmacyName = pharmacySupplierSearchForm.watch("name");
  const pharmacyAddress = pharmacySupplierSearchForm.watch("address");
  const pharmacyZip = pharmacySupplierSearchForm.watch("zip");
  const isPharmacyFormFilledOut = !!(
    pharmacyNpi?.length > 0
    || pharmacyName?.length > 0
    || pharmacyAddress?.length > 0
    || pharmacyZip?.length > 0
  );

  const {
    data: pharmacyData,
    isLoading: isLoadingPharmacyResults,
    refetch: refetchPharmacies,
  } = useListDMRPharmaciesQuery(
    {
      npi: pharmacyNpi?.length > 0 ? pharmacyNpi : null,
      name: pharmacyName?.length > 0 ? pharmacyName : null,
      address: pharmacyAddress?.length > 0 ? pharmacyAddress : null,
      zip: pharmacyZip?.length > 0 ? pharmacyZip : null,
      page_number: pharmacyResultsPageNumber,
      results_per_page: PHARMACY_RESULTS_PER_PAGE,
    },
    false,
  );

  const onSubmitPharmacySearchForm = useCallback(async () => {
    if (isPharmacyFormFilledOut) {
      setPharmacyNotSelectedError(false);
      await refetchPharmacies();
    } else {
      setPharmacyNotSelectedError(true);
    }
  }, [refetchPharmacies, isPharmacyFormFilledOut]);

  useEffect(() => {
    if (shouldRefetchPharmacies) {
      refetchPharmacies();
    }
  }, [refetchPharmacies, pharmacyResultsPageNumber, shouldRefetchPharmacies]);

  useEffect(() => {
    setPharmacyResultsPageNumber(1);
    setShouldRefetchPharmacies(false);
  }, [pharmacyName, pharmacyZip, pharmacyAddress, pharmacyNpi]);

  const formattedPharmacies: ProviderDetails[] = useMemo(() => {
    return pharmacyData?.results?.map((pharmacyListResult: { pharmacy: Pharmacy }) => {
      return {
        npi: pharmacyListResult.pharmacy.npi,
        name: pharmacyListResult.pharmacy.name,
        addressLine1: pharmacyListResult.pharmacy.address_line_1,
        addressLine2: pharmacyListResult.pharmacy.address_line_2,
        city: pharmacyListResult.pharmacy.city,
        state: pharmacyListResult.pharmacy.state,
        zip: pharmacyListResult.pharmacy.zip,
        phone: pharmacyListResult.pharmacy.phone,
      };
    });
  }, [pharmacyData]);

  // Form Driven Prescriber Search Data
  const prescriberSearchForm = useForm<DMRPrescriberSearchForm>({ defaultValues: {} });

  const prescriberNpi = prescriberSearchForm.watch("npi");
  const isPrescriberFormFilledOut = prescriberNpi?.length > 0;

  const {
    data: prescriberData,
    isLoading: isLoadingPrescriberData,
    refetch: refetchProviderData,
  } = useListProvidersQuery({ npi: prescriberNpi }, false);

  const formattedPrescribers: ProviderDetails[] = useMemo(() => {
    return prescriberData?.results?.map((prescriberListResult: { provider: Provider }) => {
      return {
        npi: prescriberListResult.provider.npi,
        name: prescriberListResult.provider.provider_first_name + " " + prescriberListResult.provider.provider_last_name,
        addressLine1: prescriberListResult.provider.provider_business_practice_address_line_1,
        addressLine2: prescriberListResult.provider.provider_business_practice_address_line_2,
        city: prescriberListResult.provider.provider_business_practice_city,
        state: prescriberListResult.provider.provider_business_practice_state,
        zip: prescriberListResult.provider.provider_business_practice_zip_code,
        phone: prescriberListResult.provider.provider_business_practice_phone,
      };
    });
  }, [prescriberData]);

  const { smallFieldsColumns, mediumFieldsColumns, largeFieldsColumns } = useResponsive({
    smallFieldsColumns: {
      desktop: 2,
      tablet: 4,
      mobile: 12,
    },
    mediumFieldsColumns: {
      desktop: 3,
      tablet: 6,
      mobile: 12,
    },
    largeFieldsColumns: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
  });

  const dmrReasonsForm = useForm<DMRDetailsForm>({
    defaultValues: {
      reasons: [],
    },
  });

  useEffect(() => {
    queryClient.removeQueries({ queryKey: ["prescriberList"] });
  }, []);

  const reasons = dmrReasonsForm.watch("reasons");
  const isCompoundPrescriptionSelected = reasons?.includes?.("compound_prescription");
  const isCoordinationOfBenefitsSelected = reasons?.includes?.("coordination_of_benefits");
  const isVaccineSelected = reasons?.includes?.("received_vaccine_in_outpatient_setting");
  const isOtherSelected = reasons?.includes?.("other");

  const onSubmitPrescriberSearchForm = useCallback(async () => {
    if (isPrescriberFormFilledOut) {
      setPrescriberNotSelectedError(false);
      await refetchProviderData();
    }
  }, [isPrescriberFormFilledOut, refetchProviderData]);

  const prescriptionVaccineEquipmentSearchForm = useForm<DMRPrescriptionVaccineEquipmentSearchForm>({ defaultValues: {} });

  const prescriptionNdc = prescriptionVaccineEquipmentSearchForm.watch("ndc");
  const isPrescriptionFormFilledOut = prescriptionNdc?.length > 0;

  const {
    data: prescriptionVaccineEquipmentResults,
    isLoading: isLoadingPrescriptionVaccineEquipmentResults,
    refetch: refetchDrugs,
  } = useListDMRDrugsQuery({ ndc: prescriptionNdc?.replace(/-/g, "") }, false);

  const onSubmitPrescriptionVaccineEquipmentSearchForm = useCallback(async () => {
    if (isPrescriptionFormFilledOut) {
      setPrescriptionNotSelectedError(false);
      await refetchDrugs();
    } else {
      setPrescriptionNotSelectedError(true);
    }
  }, [isPrescriptionFormFilledOut, refetchDrugs]);

  const claimEvidenceForm = useForm<ClaimEvidenceForm>({ shouldFocusError: false });
  const contactInformationForm = useForm<ContactInformationForm>({ shouldFocusError: false });
  const acknowledgeForm = useForm<AcknowledgeForm>({ shouldFocusError: false });

  const submitReasonsForm = dmrReasonsForm.handleSubmit(
    () => {},
    () => {},
  );

  const submitContactInformationForm = contactInformationForm.handleSubmit(
    () => {},
    () => {},
  );

  const submitClaimEvidenceForm = claimEvidenceForm.handleSubmit(
    () => {},
    () => {},
  );

  const submitAcknowledgeForm = acknowledgeForm.handleSubmit(
    () => {},
    () => {},
  );

  const submitAdditionalPrescriptionForm = async () => {
    if (!selectedPrescriptionVaccineEquipment?.ndc || !dmrAdditionalPrescriptionInformationFormRef?.current) {
      return null;
    }
    const additionalPrescriptionFormData = await dmrAdditionalPrescriptionInformationFormRef.current!.submit();
    return additionalPrescriptionFormData ? {
      ndc: selectedPrescriptionVaccineEquipment.ndc,
      date_of_fill: additionalPrescriptionFormData.purchased_date,
      rx_number: additionalPrescriptionFormData.prescription_number,
      total_paid: additionalPrescriptionFormData.total_paid,
      admin_fee: additionalPrescriptionFormData.admin_fee,
    } as DirectMemberReimbursementPharmacyPrescription : null;
  };

  const submitCompoundPrescriptionForm = async () => {
    if (!dmrCompoundPrescriptionFormRef?.current) {
      return null;
    }
    const compoundPrescriptionFormData = await dmrCompoundPrescriptionFormRef.current!.submit();
    return compoundPrescriptionFormData ? {
        rx_number: compoundPrescriptionFormData.rx_number,
        day_supply: compoundPrescriptionFormData.day_supply,
        compound_details: compoundPrescriptionFormData.ingredients.map((ingredient) => ({
          ndc: ingredient.ndc,
          quantity: ingredient.quantity,
          cost: ingredient.ingredient_cost,
        }
      )),
    } as DirectMemberReimbursementCompoundPrescriptionInformation : null;
  };

  const submitVaccineEquipmentForm = async () => {
    if (!dmrVaccineInformationFormRef?.current) {
      return null;
    }
    const vaccineInformationFormData = await dmrVaccineInformationFormRef.current!.submit();
    return vaccineInformationFormData ? {
        multiple_dose: vaccineInformationFormData.is_multi_dose === "yes",
        dose_series: vaccineInformationFormData.dose_series_number,
        administered_location: vaccineInformationFormData.administered_location,
      } as DirectMemberReimbursementVaccineInformation : null;
  };

  const scrollErrorsIntoView = () => {
    const formAnchorRefPairs: Array<[boolean, RefObject<HTMLSpanElement>]> = [
      [pharmacySupplierSearchForm.formState.isValid && !!selectedPharmacy, dmrPharmacyAnchorRef],
      [prescriberSearchForm.formState.isValid && !!selectedPrescriber, dmrPrescriberAnchorRef],
      [!!selectedPrescriptionVaccineEquipment && dmrAdditionalPrescriptionInformationFormRef?.current?.isValid, dmrPrescriptionAnchorRef],
      [claimEvidenceForm.formState.isValid, dmrEvidenceClaimAnchorRef],
      [contactInformationForm.formState.isValid, dmrContactInformationAnchorRef],
      [acknowledgeForm.formState.isValid, dmrAcknowledgeAnchorRef],
    ];

    // only scroll to the topmost form that has an error
    for (const anchorPair of formAnchorRefPairs) {
      if (!anchorPair[0]) {
        showAlert({ type: "error", style: "toast", message: translate(`${TRANSLATION_PATH}.errorSubmittingDMR`) });
        anchorPair[1].current.scrollIntoView({ block: "center" });
        break;
      }
    }
  };

  const validateAndSubmitDMRClaimDetailsHandler = async () => {
    setPharmacyNotSelectedError(!selectedPharmacy);
    setPrescriberNotSelectedError(!selectedPrescriber);
    setPrescriptionNotSelectedError(!selectedPrescriptionVaccineEquipment);

    const submitTasks = [
      submitReasonsForm(),
      submitAcknowledgeForm(),
      submitContactInformationForm(),
      submitClaimEvidenceForm(),
    ];

    await Promise.all(submitTasks).finally(scrollErrorsIntoView);

    const reasonsFormIsValid = dmrReasonsForm.formState.isValid;
    const acknowledgeFormIsValid = acknowledgeForm.formState.isValid;
    const contactInformationFormIsValid = contactInformationForm.formState.isValid;
    const claimEvidenceFormIsValid = claimEvidenceForm.formState.isValid;

    const additionalPrescriptionData = await submitAdditionalPrescriptionForm();
    const compoundPrescriptionData = isCompoundPrescriptionSelected && await submitCompoundPrescriptionForm();
    const vaccineInformationData = isVaccineSelected && await submitVaccineEquipmentForm();

    const coordinationOfBenefitsFormData = isCoordinationOfBenefitsSelected &&
      await dmrCoordinationOfBenefitsFormRef?.current?.submit();

    const coordinationOfBenefitsData = coordinationOfBenefitsFormData ? {
        on_job_inquiry: coordinationOfBenefitsFormData.on_job_inquiry === "yes",
        other_insurance_plan: coordinationOfBenefitsFormData.other_insurance_plan === "yes",
        employer_insurance_name: coordinationOfBenefitsFormData.employer_insurance_name,
        id_number: coordinationOfBenefitsFormData.id_number,
        phone_number: coordinationOfBenefitsFormData.phone_number,
        primary_carriers_explanation_of_benefits: coordinationOfBenefitsFormData.primary_carriers_explanation_of_benefits.map(
          (dmrDocument) => ({
            file_name: dmrDocument.name,
            description: "Pharmacy DMR Primary Carrier's Explanation of Benefits Document",
            name: "Pharmacy DMR Primary Carrier's Explanation of Benefits Document",
          }),
        ),
    } : null;
    const coordinationOfBenefitsDocuments = coordinationOfBenefitsFormData?.primary_carriers_explanation_of_benefits;

    const fullFormIsValid = (
      reasonsFormIsValid
      && contactInformationFormIsValid
      && claimEvidenceFormIsValid
      && acknowledgeFormIsValid
      && !!selectedPharmacy
      && !!selectedPrescriber
      && !!selectedPrescriptionVaccineEquipment
      && !!additionalPrescriptionData
      && (!isCompoundPrescriptionSelected || !!compoundPrescriptionData)
      && (!isCoordinationOfBenefitsSelected || !!coordinationOfBenefitsData)
      && (!isCoordinationOfBenefitsSelected || !!coordinationOfBenefitsDocuments)
      && (!isVaccineSelected || !!vaccineInformationData)
    );

    if (fullFormIsValid) {
      submitDMRClaimDetailsHandler(
        additionalPrescriptionData,
        compoundPrescriptionData,
        coordinationOfBenefitsData,
        coordinationOfBenefitsDocuments,
        vaccineInformationData,
      );
    }
  };

  const submitDMRClaimDetailsHandler = (
    additionalPrescriptionData: DirectMemberReimbursementPharmacyPrescription,
    compoundPrescriptionData?: DirectMemberReimbursementCompoundPrescriptionInformation,
    coordinationOfBenefitsData?: DirectMemberReimbursementCoordinationOfBenefits,
    coordinationOfBenefitsDocuments?: FileEntry[],
    vaccineInformationData?: DirectMemberReimbursementVaccineInformation,
  ) => {
    const detailsFormData = dmrReasonsForm.getValues();
    const contactInformationData = contactInformationForm.getValues();
    const claimEvidenceData = claimEvidenceForm.getValues();

    const payload: Partial<DirectMemberReimbursementSubmissionRequest> = {
      form_data: {
        pharmacy: {
          pharmacy_details: {
            reasons: detailsFormData.reasons,
            specific_reason: detailsFormData.otherReasonText,
            pharmacy_npi: selectedPharmacy.npi,
            prescriber_npi: selectedPrescriber.npi,
            prescription: additionalPrescriptionData,
          },
          claim_evidence_documents: claimEvidenceData.claim_evidence_files.map((dmrDocument) => ({
            file_name: dmrDocument.name,
            description: "Pharmacy DMR Claim Evidence File",
            name: "Pharmacy DMR Claim Evidence File",
          })),
        },
      },
      contact_email_address: contactInformationData.email_address.trim(),
      contact_phone_number: contactInformationData.phone_number.trim(),
    };
    const documents = [...claimEvidenceData.claim_evidence_files];

    if (isCoordinationOfBenefitsSelected) {
      payload.form_data.pharmacy.pharmacy_details.coordination_of_benefits = coordinationOfBenefitsData;
      documents.push(...coordinationOfBenefitsDocuments);
    }

    if (isCompoundPrescriptionSelected) {
      payload.form_data.pharmacy.pharmacy_details.compound_prescription_information = compoundPrescriptionData;
    }

    if (isVaccineSelected) {
      payload.form_data.pharmacy.pharmacy_details.vaccine_information = vaccineInformationData;
    }

    onSubmitCallbackHandler({ payload, documents });
  };

  return (
    <Stack spacing="large">
      <Form form={dmrReasonsForm}>
        <Stack spacing="medium">
          <Body>{translate(`${TRANSLATION_PATH}.whatAreTheReasonsForDMR`)}</Body>
          <ThemedCheckboxGroup.Field name="reasons" hideLabel required>
            {Object.keys(REASONS).map((reason: DirectMemberReimbursementPharmacyDetailsReason, index: number) => (
              <ThemedCheckboxGroup.Option value={reason} key={index}>
                {translate(`${TRANSLATION_PATH}.${REASONS[reason]}Reason`)}
              </ThemedCheckboxGroup.Option>
            ))}
          </ThemedCheckboxGroup.Field>
          {isOtherSelected && (
            <Grid>
              <Grid.Span columns={largeFieldsColumns}>
                <Textbox.Field name="otherReasonText" required label={translate(`${TRANSLATION_PATH}.otherReasonTextFieldLabel`)} />
              </Grid.Span>
            </Grid>
          )}
        </Stack>
      </Form>
      {isCompoundPrescriptionSelected && (
        <DirectMemberReimbursementCompoundPrescriptionForm ref={dmrCompoundPrescriptionFormRef} smallFieldsColumns={smallFieldsColumns} />
      )}
      {isCoordinationOfBenefitsSelected && (
        <DirectMemberReimbursementCoordinationOfBenefitsForm
          ref={dmrCoordinationOfBenefitsFormRef}
          smallFieldsColumns={smallFieldsColumns}
          mediumFieldsColumns={mediumFieldsColumns}
        />
      )}
      {isVaccineSelected && (
        <DirectMemberReimbursementVaccineInformationForm ref={dmrVaccineInformationFormRef} smallFieldsColumns={smallFieldsColumns} />
      )}
      <Form form={pharmacySupplierSearchForm} onSubmit={onSubmitPharmacySearchForm}>
        <span ref={dmrPharmacyAnchorRef} />
        <Stack spacing="medium">
          <Subheading>{translate(`${TRANSLATION_PATH}.providePharmacyOrSupplier`)}</Subheading>
          {pharmacyNotSelectedError && (
            <Body size="mini" className="error">
              {translate(`${TRANSLATION_PATH}.selectionIsRequired`)}
            </Body>
          )}
          {!selectedPharmacy && (
            <>
              <Grid>
                <Grid.Span columns={smallFieldsColumns}>
                  <Textbox.Field key="pharmacy-npi" name="npi" label={translate(`${TRANSLATION_PATH}.pharmacyNPI`)} />
                </Grid.Span>
                <Grid.Break />
                <Grid.Span columns={largeFieldsColumns}>
                  <Textbox.Field key="pharmacy-name" name="name" label={translate(`${TRANSLATION_PATH}.pharmacyName`)} />
                </Grid.Span>
                <Grid.Break />
                <Grid.Span columns={largeFieldsColumns}>
                  <Textbox.Field key="pharmacy-address" name="address" label={translate(`${TRANSLATION_PATH}.address`)} />
                </Grid.Span>
                <Grid.Break />
                <Grid.Span columns={smallFieldsColumns}>
                  <Textbox.Field key="pharmacy-zip" name="zip" label={translate(`${TRANSLATION_PATH}.zipcode`)} />
                </Grid.Span>
              </Grid>
              <StackOrShelf>
                <ThemedSubmitButton appearance="secondary">{translate(`${TRANSLATION_PATH}.search`)}</ThemedSubmitButton>
                <Shelf.Spacer />
              </StackOrShelf>
            </>
          )}
        </Stack>
      </Form>
      {selectedPharmacy && (
        <Grid spacing="medium">
          <Grid.Span columns={largeFieldsColumns}>
            <Stack spacing="medium">
              <DirectMemberReimbursementProviderDetailsCard
                setSelectedProvider={setSelectedPharmacy}
                provider={selectedPharmacy}
                isSelected={true}
              />
            </Stack>
          </Grid.Span>
        </Grid>
      )}
      {!selectedPharmacy && pharmacyData?.results && (
        <DirectMemberReimbursementPharmacySearchResults
          largeFieldsColumns={largeFieldsColumns}
          pharmacies={formattedPharmacies}
          isLoadingPharmacyResults={isLoadingPharmacyResults}
          pharmacyResultsPageNumber={pharmacyResultsPageNumber}
          pharmacyResultsPerPage={PHARMACY_RESULTS_PER_PAGE}
          setPharmacyResultsPageNumber={(page: number) => {
            setPharmacyResultsPageNumber(page);
            setShouldRefetchPharmacies(true);
          }}
          setSelectedPharmacy={(value) => {
            setPharmacyNotSelectedError(false);
            return setSelectedPharmacy(value);
          }}
          resultCount={pharmacyData?.result_count}
        />
      )}
      <Form form={prescriberSearchForm} onSubmit={onSubmitPrescriberSearchForm}>
        <span ref={dmrPrescriberAnchorRef} />
        <Stack spacing="medium">
          <Subheading>{translate(`${TRANSLATION_PATH}.providePrescriber`)}</Subheading>
          {prescriberNotSelectedError && (
            <Body size="mini" className="error">
              {translate(`${TRANSLATION_PATH}.selectionIsRequired`)}
            </Body>
          )}
          {!selectedPrescriber && (
            <>
              <Grid>
                <Grid.Span columns={smallFieldsColumns}>
                  <Textbox.Field key="prescriber-npi" name="npi" label={translate(`${TRANSLATION_PATH}.npiNumber`)} />
                </Grid.Span>
              </Grid>
              <StackOrShelf>
                <ThemedSubmitButton appearance="secondary">{translate(`${TRANSLATION_PATH}.search`)}</ThemedSubmitButton>
                <Shelf.Spacer />
              </StackOrShelf>
            </>
          )}
        </Stack>
      </Form>
      {prescriberData?.results && (
        <Grid spacing="medium">
          <Grid.Span columns={largeFieldsColumns}>
            <LoadingGroup loading={isLoadingPrescriberData}>
              <LoadingGroup.Placeholder fallback={<LoadingSkeleton count={1} style={{ height: "100px" }} />}>
                {!selectedPrescriber && formattedPrescribers?.length === 0 && <DirectMemberReimbursementNoResults isSingular={true} />}
                {!selectedPrescriber && formattedPrescribers?.length > 0 && (
                  <DirectMemberReimbursementProviderDetailsCard
                    setSelectedProvider={(value) => {
                      setPrescriberNotSelectedError(false);
                      return setSelectedPrescriber(value);
                    }}
                    provider={formattedPrescribers[0]}
                    isSelected={false}
                  />
                )}
              </LoadingGroup.Placeholder>
            </LoadingGroup>
            {selectedPrescriber && (
              <DirectMemberReimbursementProviderDetailsCard
                setSelectedProvider={setSelectedPrescriber}
                provider={selectedPrescriber}
                isSelected={true}
              />
            )}
          </Grid.Span>
        </Grid>
      )}
      <Form form={prescriptionVaccineEquipmentSearchForm} onSubmit={onSubmitPrescriptionVaccineEquipmentSearchForm}>
        <span ref={dmrPrescriptionAnchorRef} />
        <Stack spacing="medium">
          <Subheading>{translate(`${TRANSLATION_PATH}.providePrescriptionVaccineEquipmentPurchase`)}</Subheading>
          {prescriptionNotSelectedError && (
            <Body size="mini" className="error">
              {translate(`${TRANSLATION_PATH}.selectionIsRequired`)}
            </Body>
          )}
          {!selectedPrescriptionVaccineEquipment && (
            <>
              <Grid>
                <Grid.Span columns={smallFieldsColumns}>
                  <Textbox.Field name="ndc" label={translate(`${TRANSLATION_PATH}.ndcOrUpc`)} />
                </Grid.Span>
              </Grid>
              <StackOrShelf>
                <ThemedSubmitButton appearance="secondary">{translate(`${TRANSLATION_PATH}.search`)}</ThemedSubmitButton>
                <Shelf.Spacer />
              </StackOrShelf>
            </>
          )}
        </Stack>
      </Form>
      {selectedPrescriptionVaccineEquipment && (
        <>
          <Grid spacing="medium">
            <Grid.Span columns={largeFieldsColumns}>
              <Stack spacing="medium">
                <DirectMemberReimbursementPrescriptionVaccineEquipmentCard
                  setSelectedPrescriptionVaccineEquipment={(value) => {
                    setPrescriptionNotSelectedError(false);
                    return setSelectedPrescriptionVaccineEquipment(value);
                  }}
                  dmrDrug={selectedPrescriptionVaccineEquipment}
                  isSelected={true}
                />
              </Stack>
            </Grid.Span>
          </Grid>
          <DirectMemberReimbursementAdditionalPrescriptionInformationForm
            ref={dmrAdditionalPrescriptionInformationFormRef}
            smallFieldsColumns={smallFieldsColumns}
          />
        </>
      )}
      {!selectedPrescriptionVaccineEquipment && prescriptionVaccineEquipmentResults?.results && (
        <DirectMemberReimbursementPrescriptionVaccineEquipmentSearchResults
          largeFieldsColumns={largeFieldsColumns}
          prescriptionVaccineEquipmentResults={prescriptionVaccineEquipmentResults}
          isLoadingPrescriptionVaccineEquipmentResults={isLoadingPrescriptionVaccineEquipmentResults}
          setSelectedPrescriptionVaccineEquipment={setSelectedPrescriptionVaccineEquipment}
        />
      )}
      <Form form={claimEvidenceForm}>
        <span ref={dmrEvidenceClaimAnchorRef} />
        <DirectMemberReimbursementUploadClaimEvidenceField
          label={translate(`${TRANSLATION_PATH}.selectFiles`)}
          name="claim_evidence_files"
        />
      </Form>
      <Form form={contactInformationForm}>
        <span ref={dmrContactInformationAnchorRef} />
        <DirectMemberReimbursementContactInformationFields
          smallColumns={smallFieldsColumns}
          mediumColumns={mediumFieldsColumns}
          emailAddressFieldName="email_address"
          phoneNumberFieldName="phone_number"
        />
      </Form>
      <Form form={acknowledgeForm}>
        <span ref={dmrAcknowledgeAnchorRef} />
        <DirectMemberReimbursementAcknowledgeField fieldName="acknowledge" />
      </Form>
      <StackOrShelf>
        <ThemedButton
          appearance="primary"
          onClick={() => {
            setSubmittingPayload(true);
            validateAndSubmitDMRClaimDetailsHandler().finally(() => {
              setSubmittingPayload(false);
            });
          }}
          disabled={isEmulating || submittingPayload || isSubmitting}>
            {translate(`${TRANSLATION_PATH}.submit`)}
        </ThemedButton>
        <Shelf.Spacer />
      </StackOrShelf>
    </Stack>
  );
};
